import {
	TBeforeAfter,
	TgetChartsValues,
	TgetTableValues,
	TInvestmentFormValues,
	TtableForRowRenderData,
} from '../types'

import { DEFAULT_ROW_DATA } from '../components/Calculations/const'

const getLostCustomersRowData = (
	previousRowPayingCustomers: TBeforeAfter,
	avgChurnRate: TBeforeAfter,
) => ({
	before: Math.floor(previousRowPayingCustomers.before * avgChurnRate.before),
	after: Math.floor(previousRowPayingCustomers.after * avgChurnRate.after),
})

const getPayingCustomersRowData = (
	previousRowPayingCustomers: TBeforeAfter,
	lostCustomers: TBeforeAfter,
	newCustomers: TBeforeAfter,
) => ({
	before:
		previousRowPayingCustomers.before -
		lostCustomers.before +
		newCustomers.before,
	after:
		previousRowPayingCustomers.after - lostCustomers.after + newCustomers.after,
})

const getMMRRowData = (
	payingCustomers: TBeforeAfter,
	avgMRR: TBeforeAfter,
) => ({
	before: payingCustomers.before * avgMRR.before,
	after: payingCustomers.after * avgMRR.after,
})
const getRevenueGeneratedRowData = (
	previousRowRevenueGenerated: TBeforeAfter,
	mmr: TBeforeAfter,
) => ({
	before: previousRowRevenueGenerated.before + mmr.before,
	after: previousRowRevenueGenerated.after + mmr.after,
})
const getTotalInvestment = (
	previousTotalInvestment: number,
	investmentFormData: TInvestmentFormValues,
	index: number,
) => {
	const { frequency, amount, duration } = investmentFormData
	if (frequency === 'oneTime') return amount
	if (index === 0) return amount
	return duration > index
		? previousTotalInvestment + amount
		: previousTotalInvestment
}

const getTotalProfitRowData = (
	revenueGenerate: TBeforeAfter,
	totalInvestment: number,
) => {
	return revenueGenerate.after - revenueGenerate.before - totalInvestment
}

const getTotalROIRowData = (totalProfit: number, totalInvestment: number) =>
	Math.floor((totalProfit / totalInvestment) * 100)

export const getTableValues: TgetTableValues = (
	currentWebsiteFormData,
	newWebsiteFormData,
	investmentFormData,
	monthsForChart,
) => {
	const avgChurnRate = {
		before: 1 / currentWebsiteFormData.retention,
		after: 1 / newWebsiteFormData.retention,
	}

	return Array.from(Array(monthsForChart ? monthsForChart : 0)).reduce(
		(acc: TtableForRowRenderData[], _, index) => {
			const [previousRow] = acc.slice(-1)
			const isDelayed = index < investmentFormData.delay

			const newCustomers = {
				before: Math.floor(currentWebsiteFormData.customers),
				after: isDelayed
					? Math.floor(currentWebsiteFormData.customers)
					: Math.floor(newWebsiteFormData.customers),
			}

			const lostCustomers = getLostCustomersRowData(
				previousRow.payingCustomers,
				avgChurnRate,
			)

			const payingCustomers = getPayingCustomersRowData(
				previousRow.payingCustomers,
				lostCustomers,
				newCustomers,
			)

			const mmr = getMMRRowData(payingCustomers, {
				before: currentWebsiteFormData.avgMRR,
				after: newWebsiteFormData.avgMRR,
			})

			const revenueGenerated = getRevenueGeneratedRowData(
				previousRow.revenueGenerated,
				mmr,
			)

			const totalInvestment = getTotalInvestment(
				Number(previousRow.totalInvestment),
				investmentFormData,
				index,
			)

			const totalProfit = getTotalProfitRowData(
				revenueGenerated,
				totalInvestment,
			)
			const totalROI = getTotalROIRowData(totalProfit, totalInvestment)
			return [
				...acc,
				{
					newCustomers,
					lostCustomers,
					payingCustomers,
					mmr,
					revenueGenerated,
					totalInvestment,
					totalProfit,
					totalROI,
				},
			]
		},
		[DEFAULT_ROW_DATA],
	)
}

export const getChartsValues: TgetChartsValues = values =>
	values.map((el, index) => {
		const currentDate = new Date()
		currentDate.setMonth(currentDate.getMonth() + index)
		const currentMonth = currentDate.toLocaleString('en-US', { month: 'short' })
		const currentYear = currentDate.getFullYear().toString().slice(-2)
		return {
			before: el.revenueGenerated.before,
			after: el.revenueGenerated.after - el.totalInvestment,
			revenueDiff:
				el.revenueGenerated.after -
				el.totalInvestment -
				el.revenueGenerated.before,
			date: `${currentMonth} ${currentYear}`,
		}
	})
